import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Folder, FileText, Download, LogOut, 
  ChevronRight, AlertCircle, RefreshCw,
  File, Image, Video, Music
} from 'lucide-react';
import { RippleButton } from '../components/effects/RippleButton';

const API = process.env.REACT_APP_BACKEND_URL;

// Get icon based on file type
const getFileIcon = (fileName, type) => {
  if (type === 'folder') return Folder;
  
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return Image;
  if (['mp4', 'mov', 'avi', 'mkv'].includes(ext)) return Video;
  if (['mp3', 'wav', 'ogg', 'flac'].includes(ext)) return Music;
  if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) return FileText;
  return File;
};

export default function ClientPortal() {
  const [files, setFiles] = useState([]);
  const [folderName, setFolderName] = useState('');
  const [clientName, setClientName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMock, setIsMock] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('clientToken');
    const name = localStorage.getItem('clientName');
    const folder = localStorage.getItem('folderName');

    if (!token) {
      navigate('/client/login');
      return;
    }

    setClientName(name || 'Client');
    setFolderName(folder || 'Your Files');
    fetchFiles(token);
  }, [navigate]);

  const fetchFiles = async (token) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API}/api/client/files?token=${token}`);
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Session expired
          handleLogout();
          return;
        }
        throw new Error(data.detail || 'Failed to fetch files');
      }

      setFiles(data.files || []);
      setFolderName(data.folder_name || folderName);
      setIsMock(data.is_mock || false);
    } catch (err) {
      setError(err.message || 'Failed to load files');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (fileId, fileName) => {
    const token = localStorage.getItem('clientToken');
    if (!token) return;

    setDownloadingId(fileId);

    try {
      const response = await fetch(`${API}/api/client/download/${fileId}?token=${token}`);
      const data = await response.json();

      if (data.is_mock) {
        alert('Download functionality will be available once Zoho is connected.');
        return;
      }

      // Open download in new window with auth
      // Note: For production, you'd want to proxy this through your backend
      window.open(data.download_url, '_blank');
    } catch (err) {
      setError('Failed to download file');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('clientToken');
    
    if (token) {
      try {
        await fetch(`${API}/api/client/logout?token=${token}`, { method: 'POST' });
      } catch (err) {
        // Ignore logout errors
      }
    }

    localStorage.removeItem('clientToken');
    localStorage.removeItem('clientName');
    localStorage.removeItem('folderName');
    navigate('/client/login');
  };

  return (
    <main className="min-h-screen bg-cream" data-testid="client-portal-page">
      {/* Header */}
      <header className="bg-white border-b border-border/30 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-xl text-forest">Client Portal</h1>
            <p className="text-foreground/60 text-sm">Welcome, {clientName}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-foreground/60 hover:text-forest transition-colors text-sm"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-foreground/60 mb-6">
          <Folder className="w-4 h-4" />
          <span>{folderName}</span>
        </div>

        {/* Mock Data Notice */}
        {isMock && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3"
          >
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-800 font-medium text-sm">Demo Mode</p>
              <p className="text-amber-700 text-sm">
                Zoho WorkDrive integration is pending. Showing sample files for preview.
              </p>
            </div>
          </motion.div>
        )}

        {/* Error State */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
            <button
              onClick={() => fetchFiles(localStorage.getItem('clientToken'))}
              className="text-red-600 hover:text-red-800 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </motion.div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-forest/20 border-t-forest rounded-full animate-spin mx-auto mb-4" />
              <p className="text-foreground/60">Loading your files...</p>
            </div>
          </div>
        ) : (
          /* Files Grid */
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-2xl shadow-sm border border-border/30 overflow-hidden"
          >
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-sand/30 border-b border-border/30 text-sm font-medium text-foreground/60">
              <div className="col-span-6 md:col-span-5">Name</div>
              <div className="col-span-3 md:col-span-3 hidden md:block">Modified</div>
              <div className="col-span-2 hidden md:block">Size</div>
              <div className="col-span-6 md:col-span-2 text-right">Actions</div>
            </div>

            {/* Files List */}
            <AnimatePresence>
              {files.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <Folder className="w-12 h-12 text-foreground/20 mx-auto mb-4" />
                  <p className="text-foreground/60">No files found in your folder</p>
                </div>
              ) : (
                files.map((file, index) => {
                  const FileIcon = getFileIcon(file.name, file.type);
                  const isFolder = file.type === 'folder';
                  const isDownloading = downloadingId === file.id;

                  return (
                    <motion.div
                      key={file.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-border/20 last:border-0 hover:bg-sand/20 transition-colors items-center"
                    >
                      {/* File Name */}
                      <div className="col-span-6 md:col-span-5 flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isFolder ? 'bg-amber-100 text-amber-600' : 'bg-forest/10 text-forest'
                        }`}>
                          <FileIcon className="w-5 h-5" />
                        </div>
                        <span className="text-foreground font-medium truncate" title={file.name}>
                          {file.name}
                        </span>
                      </div>

                      {/* Modified Date */}
                      <div className="col-span-3 md:col-span-3 hidden md:block text-sm text-foreground/60">
                        {file.modified || '-'}
                      </div>

                      {/* Size */}
                      <div className="col-span-2 hidden md:block text-sm text-foreground/60">
                        {file.size || '-'}
                      </div>

                      {/* Actions */}
                      <div className="col-span-6 md:col-span-2 flex justify-end">
                        {isFolder ? (
                          <button className="flex items-center gap-1 text-sm text-forest hover:text-forest/70 transition-colors">
                            Open
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleDownload(file.id, file.name)}
                            disabled={isDownloading}
                            className="flex items-center gap-2 px-3 py-1.5 bg-forest/10 text-forest rounded-lg hover:bg-forest/20 transition-colors text-sm disabled:opacity-50"
                            data-testid={`download-${file.id}`}
                          >
                            {isDownloading ? (
                              <div className="w-4 h-4 border-2 border-forest/30 border-t-forest rounded-full animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                            Download
                          </button>
                        )}
                      </div>
                    </motion.div>
                  );
                })
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Footer Note */}
        <p className="text-center text-foreground/40 text-xs mt-8">
          Need help? Contact{' '}
          <a href="mailto:reachout@feralways.org" className="text-forest hover:underline">
            reachout@feralways.org
          </a>
        </p>
      </div>
    </main>
  );
}
